// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Search History Types
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export interface SearchHistoryEntry {
  keywords: string[]
  timestamp: number
  searchParams: {
    display: number
    sort: string
  }
}

interface SearchHistoryStore {
  entries: SearchHistoryEntry[]
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Constants
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const HISTORY_KEY = 'keynews-search-history'
const MAX_HISTORY_ENTRIES = 20

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Storage Operations
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function getHistoryStore(): SearchHistoryStore {
  try {
    const stored = localStorage.getItem(HISTORY_KEY)
    if (!stored) return { entries: [] }

    const parsed = JSON.parse(stored) as SearchHistoryStore
    return parsed
  } catch (error) {
    console.error('Failed to parse search history:', error)
    return { entries: [] }
  }
}

function saveHistoryStore(store: SearchHistoryStore): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(store))
  } catch (error) {
    console.error('Failed to save search history:', error)
  }
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper Functions
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function areKeywordsEqual(keywords1: string[], keywords2: string[]): boolean {
  if (keywords1.length !== keywords2.length) return false

  const sorted1 = [...keywords1].sort()
  const sorted2 = [...keywords2].sort()

  return sorted1.every((keyword, index) => keyword === sorted2[index])
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Public API
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export const searchHistory = {
  /**
   * Add search to history (most recent first)
   * Removes duplicates and maintains max 20 entries
   */
  add(keywords: string[], params: { display: number; sort: string }): void {
    if (keywords.length === 0) return

    const store = getHistoryStore()

    // Remove any existing entry with same keywords
    const filteredEntries = store.entries.filter(
      (entry) => !areKeywordsEqual(entry.keywords, keywords)
    )

    // Add new entry at the beginning
    const newEntry: SearchHistoryEntry = {
      keywords,
      timestamp: Date.now(),
      searchParams: params,
    }

    filteredEntries.unshift(newEntry)

    // Keep only last MAX_HISTORY_ENTRIES
    const trimmedEntries = filteredEntries.slice(0, MAX_HISTORY_ENTRIES)

    saveHistoryStore({ entries: trimmedEntries })

    // Dispatch custom event for real-time updates
    window.dispatchEvent(new CustomEvent('search-history-updated'))
  },

  /**
   * Get all search history entries (most recent first)
   */
  getAll(): SearchHistoryEntry[] {
    const store = getHistoryStore()
    return store.entries
  },

  /**
   * Get recent N search history entries
   */
  getRecent(count: number = 10): SearchHistoryEntry[] {
    const store = getHistoryStore()
    return store.entries.slice(0, count)
  },

  /**
   * Clear specific history entry by keywords
   */
  remove(keywords: string[]): void {
    const store = getHistoryStore()
    const filteredEntries = store.entries.filter(
      (entry) => !areKeywordsEqual(entry.keywords, keywords)
    )
    saveHistoryStore({ entries: filteredEntries })
  },

  /**
   * Clear all search history
   */
  clearAll(): void {
    localStorage.removeItem(HISTORY_KEY)
  },

  /**
   * Check if specific keywords exist in history
   */
  has(keywords: string[]): boolean {
    const store = getHistoryStore()
    return store.entries.some((entry) => areKeywordsEqual(entry.keywords, keywords))
  },

  /**
   * Get unique keywords from history (for autocomplete/suggestions)
   */
  getUniqueKeywords(): string[] {
    const store = getHistoryStore()
    const keywordSet = new Set<string>()

    store.entries.forEach((entry) => {
      entry.keywords.forEach((keyword) => keywordSet.add(keyword))
    })

    return Array.from(keywordSet)
  },

  /**
   * Get history statistics
   */
  getStats(): {
    totalEntries: number
    totalUniqueKeywords: number
    oldestEntry: number | null
    newestEntry: number | null
  } {
    const store = getHistoryStore()
    const uniqueKeywords = this.getUniqueKeywords()

    let oldest: number | null = null
    let newest: number | null = null

    if (store.entries.length > 0) {
      newest = store.entries[0].timestamp
      oldest = store.entries[store.entries.length - 1].timestamp
    }

    return {
      totalEntries: store.entries.length,
      totalUniqueKeywords: uniqueKeywords.length,
      oldestEntry: oldest,
      newestEntry: newest,
    }
  },
}
